import {
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  Text,
  BlockStack,
  EmptyState,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Customer, FinanceHistorySummary } from "types/Customer";
import "./customer-finance-history.scss";

interface Props {
  customer: Customer;
  companyId: string;
  onCreateNewYear: () => void;
}

export default function CustomerFinanceHistory({
  customer,
  companyId,
  onCreateNewYear,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusBadge = (record: FinanceHistorySummary) => {
    if (record.status === "draft") {
      return <Badge>{t("draft")}</Badge>;
    }

    const isExpired = new Date(record.validUntil) < new Date();

    if (record.isFinanceActive && !isExpired) {
      return <Badge tone="success">{t("active")}</Badge>;
    }
    if (isExpired) {
      return <Badge tone="critical">{t("expired")}</Badge>;
    }
    if (record.isFinanceFormSent) {
      return <Badge tone="attention">{t("pending_signature")}</Badge>;
    }
    if (record.isApproved) {
      return <Badge tone="info">{t("approved")}</Badge>;
    }
    return <Badge tone="warning">{t("status_pending_approval")}</Badge>;
  };

  const handleViewDetails = (record: FinanceHistorySummary) => {
    const url = `/app/${companyId}/customers/${customer.id}/finance/${record.year}/${record.id}`;
    navigate(url);
  };

  const financeHistory = customer.financeHistory || [];

  const rows = financeHistory.map((record) => [
    record.year.toString(),
    getStatusBadge(record),
    new Date(record.validUntil).toLocaleDateString(),
    <Button onClick={() => handleViewDetails(record)} size="slim">
      {t("view_details")}
    </Button>,
  ]);

  return (
    <Card roundedAbove="sm">
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {t("finance_approvals")}
          </Text>
          <Button variant="primary" onClick={onCreateNewYear}>
            {t("create_new_year_approval")}
          </Button>
        </InlineStack>

        {financeHistory.length === 0 ? (
          <EmptyState
            heading={t("no_finance_approvals")}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>{t("no_finance_approvals_description")}</p>
            <Button variant="primary" onClick={onCreateNewYear}>
              {t("create_first_approval")}
            </Button>
          </EmptyState>
        ) : (
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={[
              t("year"),
              t("status"),
              t("valid_until"),
              t("actions"),
            ]}
            rows={rows}
          />
        )}
      </BlockStack>
    </Card>
  );
}
